import {
  ACHResponse,
  CreditCardResponse,
  PaymentsService,
} from "client/openapi";
import BankOption from "components/PaymentMethods/bank";
import CardOption from "components/PaymentMethods/cards";
import RequestPaymentMethodForm from "components/PaymentMethods/RequestPaymentMethodForm";
import { useEffect, useState } from "react";
import { StepDialogProps, StepFrame } from "./shared";

/**
 * The Payment method step (students and parents). Done: the cards and
 * banks on file, as the billing page lists them (a student's include their
 * parents'). Not yet: ask for one by text or email.
 */
export default function PaymentMethodStepDialog({
  orgId,
  person,
  done,
  onDone,
  onClose,
}: StepDialogProps) {
  const student = person.kind === "student";
  const [cards, setCards] = useState<CreditCardResponse[]>();
  const [banks, setBanks] = useState<ACHResponse[]>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!done) return;
    const id = person.id;
    Promise.all(
      student
        ? [
            PaymentsService.getStudentCards({ studentId: id }),
            PaymentsService.getStudentAchAccounts({ studentId: id }),
          ]
        : [
            PaymentsService.getParentCards({ parentId: id }),
            PaymentsService.getParentAchAccounts({ parentId: id }),
          ]
    )
      .then(([foundCards, foundBanks]) => {
        setCards(foundCards as CreditCardResponse[]);
        setBanks(foundBanks as ACHResponse[]);
      })
      .catch(() => setFailed(true));
  }, [done, student, person.id]);

  if (!done) {
    return (
      <StepFrame title="Payment method" person={person} onClose={onClose}>
        <p className="mb-3 text-sm text-gray-600">
          No card or bank on file yet. Ask for one by text or email:
        </p>
        <RequestPaymentMethodForm
          orgId={orgId}
          student={student ? { id: person.id } : undefined}
          parent={student ? undefined : { id: person.id }}
          onSent={onDone}
        />
      </StepFrame>
    );
  }

  return (
    <StepFrame title="Payment method" person={person} onClose={onClose}>
      {failed && (
        <p className="text-sm text-red-600">
          The payment methods could not be loaded. Try again.
        </p>
      )}
      {!failed && (!cards || !banks) && (
        <p className="text-sm text-gray-500">Loading...</p>
      )}
      {cards && banks && cards.length + banks.length === 0 && (
        <p className="text-sm text-gray-600">No card or bank on file.</p>
      )}
      {cards && cards.length > 0 && (
        <section className="mb-4">
          <h3 className="mb-1 text-sm font-bold text-gray-900">Cards</h3>
          <table className="w-full">
            <tbody>
              {cards.map((card) => (
                <CardOption key={card.payment_method_id} card={card} />
              ))}
            </tbody>
          </table>
        </section>
      )}
      {banks && banks.length > 0 && (
        <section>
          <h3 className="mb-1 text-sm font-bold text-gray-900">
            Bank accounts
          </h3>
          <table className="w-full">
            <tbody>
              {banks.map((bank) => (
                <BankOption key={bank.payment_method_id} bank={bank} />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </StepFrame>
  );
}
